import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Sparkles,
    Pin,
    PenTool,
    FlaskConical,
    Loader2,
    CreditCard,
    MessageCircle,
} from "lucide-react";
import {
    adminGetFeatureFlags,
    adminUpdateFeatureFlags,
} from "../../lib/api";
import { resetFeatureFlagsCache } from "../../lib/feature_flags";
import { useAuth } from "../../lib/auth";

/**
 * "Future" admin tab.
 *
 * One switch per unfinished feature. When OFF (default) the matching
 * UI gets hidden across the app — the underlying backend code stays
 * intact so we can refine and re-enable later. Flag list is pulled
 * from the backend so adding a new feature flag = one backend constant
 * + one row here.
 */

const FLAG_META = {
    show_auto_pin: {
        title: "Auto-pin applicable pages",
        description:
            "When the AI estimate finishes, every doc it used gets pinned to the top of the sidebar. Currently misfires on multi-doc projects — hide until polished.",
        icon: Pin,
        accent: "#00A3FF",
    },
    show_draw_takeoff: {
        title: "Draw Takeoff (Beta)",
        description:
            "Lets the AI auto-draw areas, linear runs and counts on every page after an estimate. Vision accuracy still inconsistent — hide for launch.",
        icon: PenTool,
        accent: "#00C853",
    },
    // iter-108: subscriptions visibility kill-switch. Superadmin only.
    show_subscriptions: {
        title: "Subscriptions",
        description:
            "When OFF, the Subscription menu item, credit balance pill, and paywall redirects are hidden across the app. Internal users keep working without ever seeing billing UI.",
        icon: CreditCard,
        accent: "#7B5BFF",
        superadminOnly: true,
    },
    // iter-111: workgroup team chat kill-switch. Superadmin only.
    show_workgroup_chat: {
        title: "Workgroup team chat",
        description:
            "When OFF, the realtime chat panel + its new-message notifications are hidden from every workgroup. Useful if crew chatter is happening elsewhere and you want a cleaner workgroup view.",
        icon: MessageCircle,
        accent: "#38BDF8",
        superadminOnly: true,
    },
};

export default function FutureTab() {
    const { user } = useAuth();
    const isSuperadmin = (user?.role || "") === "superadmin";
    const [flags, setFlags] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState("");

    useEffect(() => {
        let dead = false;
        (async () => {
            setLoading(true);
            try {
                const f = await adminGetFeatureFlags();
                if (!dead) setFlags(f);
            } catch (err) {
                toast.error(
                    err?.response?.data?.detail || "Couldn't load flags.",
                );
            } finally {
                if (!dead) setLoading(false);
            }
        })();
        return () => {
            dead = true;
        };
    }, []);

    const setFlag = async (key, value) => {
        setSaving(key);
        // Optimistic.
        setFlags((s) => ({ ...s, [key]: value }));
        try {
            const next = await adminUpdateFeatureFlags({ [key]: value });
            setFlags(next);
            resetFeatureFlagsCache();
            toast.success(`${FLAG_META[key]?.title || key} ${value ? "enabled" : "hidden"}`);
        } catch (err) {
            // Revert.
            setFlags((s) => ({ ...s, [key]: !value }));
            toast.error(err?.response?.data?.detail || "Couldn't save.");
        } finally {
            setSaving("");
        }
    };

    const knownKeys = Object.keys(FLAG_META).filter(
        (k) => !FLAG_META[k].superadminOnly || isSuperadmin,
    );

    return (
        <div className="space-y-5" data-testid="admin-future">
            <section
                className="bg-[#131A2F]/80 border border-[#2A3655] rounded-md overflow-hidden"
                data-testid="future-flags-panel"
            >
                <header className="flex items-center justify-between px-5 py-3 border-b border-[#2A3655] bg-[#1C253C]">
                    <h2 className="text-base font-bold text-white tracking-tight flex items-center gap-2">
                        <FlaskConical className="w-4 h-4 text-amber-400" />
                        Future / experimental features
                    </h2>
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                        Hidden when off
                    </span>
                </header>
                <div className="p-5">
                    <p className="text-xs text-slate-400 mb-4">
                        Each switch controls whether an unfinished feature is
                        visible across the user-facing app. Turning a flag{" "}
                        <span className="text-rose-300 font-semibold">off</span>{" "}
                        fully removes the corresponding UI — backend code stays
                        in place so we can refine and turn it back on later.
                    </p>
                    {loading ? (
                        <div className="text-sm text-slate-500">Loading…</div>
                    ) : (
                        <div className="space-y-3">
                            {knownKeys.map((k) => {
                                const meta = FLAG_META[k];
                                const Icon = meta.icon;
                                const on = !!flags[k];
                                const isBusy = saving === k;
                                return (
                                    <div
                                        key={k}
                                        className="flex items-start gap-3 px-4 py-3 bg-[#0A1128] border border-[#2A3655] rounded-sm"
                                        data-testid={`future-flag-row-${k}`}
                                    >
                                        <Icon
                                            className="w-4 h-4 mt-0.5 shrink-0"
                                            style={{ color: meta.accent }}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-bold text-white">
                                                {meta.title}
                                            </div>
                                            <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">
                                                {meta.description}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            role="switch"
                                            aria-checked={on}
                                            onClick={() => setFlag(k, !on)}
                                            disabled={isBusy}
                                            data-testid={`future-flag-toggle-${k}`}
                                            className={`relative inline-flex shrink-0 h-6 w-11 rounded-full transition-colors disabled:opacity-50 ${
                                                on
                                                    ? "bg-[#007AFF]"
                                                    : "bg-[#2A3655]"
                                            }`}
                                        >
                                            <span
                                                className={`absolute top-0.5 inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                                                    on ? "translate-x-5" : "translate-x-0.5"
                                                }`}
                                            />
                                            {isBusy && (
                                                <Loader2 className="w-3 h-3 absolute -right-5 top-1.5 animate-spin text-slate-400" />
                                            )}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>

            <div className="px-1 text-[11px] text-slate-500 inline-flex items-center gap-1.5">
                <Sparkles className="w-3 h-3 text-amber-400" />
                Toggles take effect on the user's next page load (or after
                they refresh).
            </div>
        </div>
    );
}
